import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ScoreSlider from "@/components/ScoreSlider";
import { useAuth } from "@/context/AuthContext";

const SYMPTOMS = [
  "Hot flashes", "Night sweats", "Brain fog", "Fatigue",
  "Anxiety/stress", "Mood changes", "Aches/joint discomfort", "Other",
];

export default function Checkin() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const day = params.get("day");

  const [sleep, setSleep] = useState(5);
  const [energy, setEnergy] = useState(5);
  const [mood, setMood] = useState(5);
  const [wellbeing, setWellbeing] = useState(5);
  const [notes, setNotes] = useState("");
  const [symptoms, setSymptoms] = useState({}); // name -> severity
  const [busy, setBusy] = useState(false);

  const toggleSymptom = (name) => {
    setSymptoms((prev) => {
      const next = { ...prev };
      if (name in next) delete next[name];
      else next[name] = 5;
      return next;
    });
  };
  const setSeverity = (name, val) => setSymptoms((prev) => ({ ...prev, [name]: val }));

  const save = async () => {
    setBusy(true);
    try {
      const symptomList = Object.entries(symptoms).map(([name, severity]) => ({ name, severity }));
      await api.post("/checkins", {
        sleep_score: sleep,
        energy_score: energy,
        mood_score: mood,
        wellbeing_score: wellbeing,
        notes,
        symptoms: symptomList,
      });
      if (day) {
        const { data } = await api.post(`/days/${day}/complete`);
        await refresh();
        if (data.is_final) {
          navigate("/final");
        } else {
          navigate(`/complete/${day}`, { state: data });
        }
      } else {
        toast.success("Check in saved");
        navigate("/home");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-app" data-testid="checkin-page">
      <div className="app-frame px-5 pt-6 pb-32">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-ink-muted mb-4" data-testid="checkin-back">
          <ChevronLeft size={20} /> Back
        </button>

        <div className="animate-fade-rise">
          <p className="overline-label">{day ? "Daily check in" : "Update your check in"}</p>
          <h1 className="font-serif text-3xl font-bold text-ink mt-2">How are you today?</h1>
          <p className="text-ink-soft text-sm mt-2">There are no wrong answers. Just notice where you are.</p>
        </div>

        <div className="mt-6 space-y-3">
          <ScoreSlider label="Sleep" value={sleep} onChange={setSleep} testId="checkin-sleep" lowLabel="Very poor" highLabel="Excellent" />
          <ScoreSlider label="Energy" value={energy} onChange={setEnergy} testId="checkin-energy" lowLabel="Drained" highLabel="Energised" />
          <ScoreSlider label="Mood / stress" value={mood} onChange={setMood} testId="checkin-mood" lowLabel="Struggling" highLabel="Great" />
          <ScoreSlider label="Overall wellbeing" value={wellbeing} onChange={setWellbeing} testId="checkin-wellbeing" lowLabel="Low" highLabel="Wonderful" />
        </div>

        {/* symptoms */}
        <div className="mt-6">
          <p className="overline-label">Any symptoms today?</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {SYMPTOMS.map((s) => {
              const active = s in symptoms;
              return (
                <button
                  key={s}
                  data-testid={`symptom-${s.replace(/[^a-z]/gi, "").toLowerCase()}`}
                  onClick={() => toggleSymptom(s)}
                  className={`text-sm font-medium px-4 py-2 rounded-full border-2 transition-colors ${
                    active ? "border-[var(--brand)] bg-sage text-ink" : "border-subtle bg-white text-ink-soft"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>

          {Object.keys(symptoms).length > 0 && (
            <div className="mt-4 space-y-3">
              {Object.entries(symptoms).map(([name, sev]) => (
                <div key={name} className="bg-white rounded-2xl p-4 card-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-ink">{name}, severity</span>
                    <span className="font-serif text-lg font-bold text-brand">{sev}</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={10}
                    value={sev}
                    onChange={(e) => setSeverity(name, Number(e.target.value))}
                    className="reset-slider"
                    data-testid={`severity-${name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <p className="overline-label">Notes (optional)</p>
          <textarea
            data-testid="checkin-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mt-3 w-full rounded-2xl border border-subtle bg-white px-4 py-3 text-ink outline-none focus:border-[var(--brand)] resize-none"
            placeholder="Anything you'd like to remember about today…"
          />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 backdrop-blur-xl border-t border-subtle" style={{ backgroundColor: "rgba(250,248,245,0.92)" }}>
        <div className="max-w-[480px] mx-auto px-5 py-4">
          <button
            data-testid="save-checkin-btn"
            onClick={save}
            disabled={busy}
            className="w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-4 rounded-full transition-colors disabled:opacity-60"
          >
            {busy ? "Saving…" : "Save Today's Check In"}
          </button>
        </div>
      </div>
    </div>
  );
}
